<template>
  <div id="page-container" v-show="showSucceedPage" v-loading = 'loading'>
    <div class="pageBodyContainer">
      <img
        class="paymentStatusIcon"
        src="@/assets/payment/success.svg"
        alt="payment successful"
      />
      <div class="paymentStatusTitle">Payment Successful</div>
      <div class="paymentStatusInfo">
        The payment has been done successfully.<br />Thanks for being there with
        us.
      </div>

      <div
        class="redirectBanner"
        v-if="this.showNextStep"
        @click="goToNextStep()"
      >
        <div class="font-bold">What's Next:&nbsp;</div>
        <div class="bannerMessage">
         {{ conditional_guidance_wording }}
        </div>
      </div>

      <ActionRequiredDialog v-if = "schedule_blood_draw_dialog_visibility"
      @close = "schedule_blood_draw_dialog_visibility = false"/>
    </div>
  </div>
</template>

<script>
import { ElNotification } from 'element-plus';
import { usePaymentStore } from "@/stores/PaymentStore.ts";
import { mapState, mapActions} from "pinia";
import ActionRequiredDialog from '@/components/shippingAddress/ActionRequiredDialog.vue';

export default {
  name: "PaymentSucceed",
  components:{
      ActionRequiredDialog
  },
  data() {
    return {
      schedule_blood_draw_dialog_visibility:false,
      loading:false,
      showSucceedPage: false,
    };
  },
  async mounted() {
    this.loading = true;
    window.scrollTo(0, 0);
    try{
      // If token, sample_id or accession_id in PaymentStore are null or empty, means user refresh at the current page:
      // -> NOT allow to directly show Payment Succeed. If refresh at /payment_succeed, go to /paymentMedia (PaymentMedia) to start checking from beginning
      if(!this.sample_id || !this.accession_id || !this.token){
        this.$router.push({
          name:'paymentMedia',
          query:JSON.parse(localStorage.getItem('pns_single_page_query'))
        })
        return;
      }else{
      // redirect from other pages which is not refreshing at /payment_succeed
      // NOTES: ids needed should already have been stored in the PaymentStore
        // 1. initiate all data needed
        await this.initiatePaymentSucceedPage();

        // 2. auto show action required dialog, if information complete, (legacy) concierge already paid, kit is not delivered, ship to patient(address needed)
        if(this.is_information_complete && this.is_concierge_paid && !this.is_kit_delivered && this.address_needed){
          this.schedule_blood_draw_dialog_visibility = true;
        }
      }
      this.showSucceedPage = true;
    }catch(err){
      console.log('PaymentSucceed mounted error: ', err);
      ElNotification({
          title: 'Error',
          customClass: "errorNotification",
          message: err?.message ?? 'PaymentMedia mounted error',
          type: 'error',
          duration: 0,
      });
      this.$router.push('/failed');
    }finally{
      this.loading = false;
    }
  },
  computed: {
    ...mapState(()=>usePaymentStore(), [
      'accession_id',
      'sample_id',
      'token',
      'amount_to_pay',
      'address_needed',
      'blood_draw_needed',
      'non_blood_draw_needed',
      'current_basic_and_shipping_information',
      'sample_types_params',
      'is_concierge_paid',
      'customer_id'
    ]),
    showNextStep() {
      return !this.amount_to_pay ;
    },
    is_kit_delivered(){
      return this.current_basic_and_shipping_information?.reason == 'DELIVERED';
    },
    conditional_guidance_wording(){
      if(this.is_kit_delivered || !this.address_needed){
        return 'Proceed with sample collection.'
      }
      return 'Your kit is being prepared for shipment.';
    },
    is_basic_information_complete(){
      // name, gender, DOB are required. phone is required if the country isn't US
      const output = this.current_basic_and_shipping_information?.gender 
      && this.current_basic_and_shipping_information?.birthdate 
      && this.current_basic_and_shipping_information?.name 
      && (
        !this.address_needed 
        || (
          this.address_needed 
          && (this.current_basic_and_shipping_information?.country == 'US' || (this.current_basic_and_shipping_information?.country!='US' && this.current_basic_and_shipping_information?.phone))
        )
      );
      return output;
    },
    is_shipping_information_complete(){
      const output =  this.current_basic_and_shipping_information?.street 
      && this.current_basic_and_shipping_information?.city 
      && this.current_basic_and_shipping_information?.state 
      && this.current_basic_and_shipping_information?.zipcode 
      && this.current_basic_and_shipping_information?.country;
      return output;
    },
    is_information_complete(){
      const output = this.address_needed ? (this.is_basic_information_complete && this.is_shipping_information_complete) : this.is_basic_information_complete;
      return output;
    },
  },
  methods: {
    ...mapActions(()=>usePaymentStore(),[
      'storeAllQueryInfomation',
      'initiatePaymentSucceedPage'
    ]),
    goToNextStep(){
      //If the information is complete:
      if(this.is_information_complete){
        // and if the (legacy) blood draw concierge is already paid: keep the scheduling redirect to the phleb concierge page
        if(this.is_concierge_paid){
          // if is_kit_delivered(kit to patient) or address_needed==false(kit by office):
          const zhang_jun_base_url = process.env.VUE_APP_ENV == 'production' ? 'https://phleb.vibrant-wellness.com/app/concierge-request/concierge':'https://phleb.vibrant-wellness.com/app/staging/concierge-request/concierge';
          if(this.is_kit_delivered || !this.address_needed){
            // -> jump to Zhang Jun Page for scheduling: start day is today
            const url = `${zhang_jun_base_url}/${this.accession_id}`;
            window.location.href = url;
          }else{
            // show action required dialog
              this.schedule_blood_draw_dialog_visibility = true;
          }
        }else
        // if blood draw concierge is unpaid:
          // no blood kit or mixed order (non-blood kit and blood kit) -> collections page (customized)
          // only blood kit -> blood draw site page
          return this.non_blood_draw_needed
            // NOTES: cannot use push query for /collections, since there will be '+' in sample_types_params. If using push query, the "+" will be converted to be "%2B". We need it remain as '+'.
          ? this.$router.push({
              name:'collections',
              query:{
                instructions:this.sample_types_params,
                accession_id:this.accession_id,
                has_blood_draw:this.blood_draw_needed
              }
            })
          : this.$router.push({
              name:'bloodDrawMedia',
              query:JSON.parse(localStorage.getItem('pns_single_page_query'))
            });
      }else{
      // If the information is incomplete
        // Jump to Information Section (ShippingAddressMedia) as Regular Flow
        //  remove all flow type params.
        // NOTES: driect_to_blooddraw and amount_to_pay normally SHOULDN'T be true at this step. But, in case, clear all of them (legacy links may still carry always_show_payment / concierge_needed)
        let regular_flow_query = JSON.parse(localStorage.getItem('pns_single_page_query'));
        delete regular_flow_query['payment_prioritized'];
        delete regular_flow_query['always_show_payment'];
        delete regular_flow_query['concierge_needed'];
        delete regular_flow_query['amount_to_pay'];
        delete regular_flow_query['driect_to_blooddraw'];
        this.$router.push({
          name: 'shippingAddressMedia',
          query: regular_flow_query
        })
      }
    },
  },
}

</script>

<style scoped>
.pageBodyContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 26px;
  position: relative;
}

.successBanner {
  width: 767px;
  height: 80px !important;
  border-radius: 4px;
  background: #20a8c3;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Roboto;
  font-size: 24px;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0px;
  text-align: center;
}

.redirectBanner {
  width: 767px;
  height: 80px !important;
  border-radius: 4px;
  background: #20a8c3;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Roboto;
  font-size: 24px;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0px;
  text-align: center;
  cursor: pointer;
}

.redirectBanner:hover {
  background: #30c0db;
}

@media only screen and (max-width: 767px) {
  .successBanner {
    width: 320px;
    font-size: 16px;
    display: flex;
    flex-direction: column;
  }

  .redirectBanner {
    width: 320px;
    font-size: 16px;
    display: flex;
    flex-direction: column;
  }

  .successBanner .bannerMessage::before {
    content: "\A";
  }
}
</style>
